import React, { useState } from 'react'
import { Drawer, Select, Button, Card, message } from 'antd'
import { Horizontal, Vertical, Texto } from '@gravitate-js/excalibrr'

interface UnmappedSuppliersDrawerProps {
  visible: boolean
  onClose: () => void
  unmappedSuppliers: string[]
  onMapSupplier: (values: {
    source_name: string
    source_id?: string
    counterparty_id: string
  }) => Promise<void>
  counterpartyOptions: Array<{
    supplier_id: string
    supplier_name: string
  }>
}

export function UnmappedSuppliersDrawer({
  visible,
  onClose,
  unmappedSuppliers,
  onMapSupplier,
  counterpartyOptions,
}: UnmappedSuppliersDrawerProps) {
  // Track only selected counterparties and loading state
  const [selectedCounterparties, setSelectedCounterparties] = useState<Record<string, string>>({})
  const [loadingSupplier, setLoadingSupplier] = useState<string | null>(null)

  const handleMap = async (supplierName: string) => {
    const counterpartyId = selectedCounterparties[supplierName]
    if (!counterpartyId) {
      message.warning('Please select a counterparty first')
      return
    }

    setLoadingSupplier(supplierName)

    try {
      message.loading('Adding external supplier...', 0)
      await onMapSupplier({
        source_name: supplierName,
        counterparty_id: counterpartyId,
      })

      // On success, clear the selection for this supplier
      setSelectedCounterparties((prev) => {
        const { [supplierName]: _, ...rest } = prev
        return rest
      })

      message.destroy()
      message.success('External supplier added successfully')
    } catch (error) {
      message.destroy()
      message.error(`Failed to map "${supplierName}": ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setLoadingSupplier(null)
    }
  }

  return (
    <Drawer
      title='Map External Suppliers'
      placement='right'
      onClose={onClose}
      open={visible}
      width={400}
      styles={{
        body: {
          padding: 24,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Texto appearance='hint' style={{ marginBottom: 16, flexShrink: 0 }}>
        Map external suppliers to their corresponding Gravitate counterparties to enable invoice matching
      </Texto>

      <Vertical gap={16} style={{ flex: 1, overflowY: 'auto', paddingRight: 8 }}>
        {unmappedSuppliers.map((supplier) => (
          <Card
            key={supplier}
            style={{
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <Vertical gap={12}>
              <Texto weight='bold'>{supplier}</Texto>

              <Select
                style={{ width: '100%' }}
                placeholder='Select counterparty'
                value={selectedCounterparties[supplier] || undefined}
                options={counterpartyOptions
                  .map((opt) => ({
                    value: opt.supplier_id,
                    label: opt.supplier_name,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label))}
                onChange={(value) =>
                  setSelectedCounterparties((prev) => ({
                    ...prev,
                    [supplier]: value,
                  }))
                }
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />

              <Horizontal justifyContent='flex-end'>
                <Button
                  type='primary'
                  disabled={!selectedCounterparties[supplier] || loadingSupplier === supplier}
                  onClick={() => handleMap(supplier)}
                  loading={loadingSupplier === supplier}
                >
                  Map Supplier
                </Button>
              </Horizontal>
            </Vertical>
          </Card>
        ))}
      </Vertical>
    </Drawer>
  )
}
